import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Injector } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { of } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import _isString from 'lodash/isString';
import { SimpleVideo } from '@portal/models/simpleVideo';
import { getImagesEndpoint } from '@portal/api-endpoints';
import { AGENT_TYPE } from '../../auth.constants';

const VIDEO_FRAME_CAPTURE_TIMEOUT_MS = 15_000;

@Injectable({ providedIn: 'root' })
export class VideoFrameService {
  private readonly contentApi: string = getImagesEndpoint(this.injector.get(AGENT_TYPE));

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly sanitizer: DomSanitizer,
    private readonly injector: Injector,
  ) {}

  capture(value: SimpleVideo | string | null | undefined): Observable<SafeUrl | null> {
    if (!value) {
      return of(null);
    }
    const path = _isString(value) ? value : value.path;
    if (!path) {
      return of(null);
    }
    return this.captureFromUrl(`${this.contentApi}${path}`);
  }

  private captureFromUrl(url: string): Observable<SafeUrl | null> {
    return new Observable<SafeUrl | null>(subscriber => {
      const video = this.document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.preload = 'auto';
      video.src = url;
      video.style.position = 'fixed';
      video.style.left = '-9999px';
      video.style.width = '1px';
      video.style.height = '1px';
      this.document.body.appendChild(video);

      // eslint-disable-next-line prefer-const
      let onLoaded: () => void;
      // eslint-disable-next-line prefer-const
      let onError: () => void;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      let disposed = false;

      const cleanup = () => {
        if (disposed) {
          return;
        }
        disposed = true;
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
          timeoutId = undefined;
        }
        video.removeEventListener('loadeddata', onLoaded);
        video.removeEventListener('error', onError);
        video.removeAttribute('src');
        video.load();
        video.remove();
      };

      onLoaded = () => {
        try {
          const canvas = this.document.createElement('canvas');
          canvas.width = video.videoWidth || 320;
          canvas.height = video.videoHeight || 180;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
          subscriber.next(this.sanitizer.bypassSecurityTrustUrl(dataUrl));
          subscriber.complete();
        } catch {
          subscriber.next(null);
          subscriber.complete();
        } finally {
          cleanup();
        }
      };

      onError = () => {
        subscriber.next(null);
        subscriber.complete();
        cleanup();
      };

      video.addEventListener('loadeddata', onLoaded);
      video.addEventListener('error', onError);
      timeoutId = setTimeout(onError, VIDEO_FRAME_CAPTURE_TIMEOUT_MS);

      return () => cleanup();
    });
  }
}
